import { useMemo } from 'react'

interface HeatmapData {
  day: string
  hour: number
  value: number
}

interface HeatmapChartProps {
  data?: HeatmapData[]
  title?: string
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const HOURS = Array.from({ length: 24 }, (_, i) => i)

function generateMockData(): HeatmapData[] {
  const data: HeatmapData[] = []
  DAYS.forEach((day) => {
    HOURS.forEach((hour) => {
      // Simulate higher activity during business hours
      const baseValue = hour >= 9 && hour <= 17 ? 60 : 20
      const variance = Math.random() * 40
      data.push({
        day,
        hour,
        value: Math.floor(baseValue + variance),
      })
    })
  })
  return data
}

function getColorIntensity(value: number, max: number): string {
  const intensity = value / max
  if (intensity < 0.2) return 'bg-indigo-50'
  if (intensity < 0.4) return 'bg-indigo-100'
  if (intensity < 0.6) return 'bg-indigo-200'
  if (intensity < 0.8) return 'bg-indigo-300'
  return 'bg-indigo-400'
}

export function HeatmapChart({ data, title = 'Activity Heatmap' }: HeatmapChartProps) {
  const heatmapData = useMemo(() => {
    return data || generateMockData()
  }, [data])

  const maxValue = useMemo(() => {
    return Math.max(...heatmapData.map((d) => d.value))
  }, [heatmapData])

  const dataMap = useMemo(() => {
    const map: Record<string, number> = {}
    heatmapData.forEach((item) => {
      map[`${item.day}-${item.hour}`] = item.value
    })
    return map
  }, [heatmapData])

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>

      <div className="overflow-x-auto">
        <div className="min-w-[600px]">
          {/* Hour labels */}
          <div className="flex items-center mb-2">
            <div className="w-12" />
            {HOURS.map((hour) => (
              <div key={hour} className="flex-1 text-center text-xs text-gray-500">
                {hour}
              </div>
            ))}
          </div>

          {/* Heatmap grid */}
          {DAYS.map((day) => (
            <div key={day} className="flex items-center mb-1">
              <div className="w-12 text-sm text-gray-600 font-medium">{day}</div>
              {HOURS.map((hour) => {
                const value = dataMap[`${day}-${hour}`] || 0
                return (
                  <div key={`${day}-${hour}`} className="flex-1 px-0.5">
                    <div
                      className={`h-6 rounded-sm ${getColorIntensity(value, maxValue)} hover:ring-2 hover:ring-indigo-500 cursor-pointer transition-all group relative`}
                      title={`${day} ${hour}:00 - ${value} clicks`}
                    >
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                        {value} clicks
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center mt-4 space-x-2">
        <span className="text-xs text-gray-500">Less</span>
        <div className="flex space-x-1">
          <div className="w-4 h-4 bg-indigo-50 rounded-sm" />
          <div className="w-4 h-4 bg-indigo-100 rounded-sm" />
          <div className="w-4 h-4 bg-indigo-200 rounded-sm" />
          <div className="w-4 h-4 bg-indigo-300 rounded-sm" />
          <div className="w-4 h-4 bg-indigo-400 rounded-sm" />
        </div>
        <span className="text-xs text-gray-500">More</span>
      </div>
    </div>
  )
}
